import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import communityService, {
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';

/** Parse time string (e.g. "45", "4'", "90+2") to { minute, added } for sorting. */
const parseTime = (t: string): { minute: number; added: number } => {
  const s = String(t ?? '').trim();
  const plus = s.indexOf('+');
  if (plus === -1) {
    const minute = Number.parseInt(s.replace(/\D/g, ''), 10) || 0;
    return { minute, added: 0 };
  }
  const minute = Number.parseInt(s.slice(0, plus).replace(/\D/g, ''), 10) || 0;
  const added = Number.parseInt(s.slice(plus + 1).replace(/\D/g, ''), 10) || 0;
  return { minute, added };
};

const sortTimelineByTimeDesc = (events: GameTimelineEvent[]): GameTimelineEvent[] => {
  return [...events].sort((a, b) => {
    const ta = parseTime(a.time);
    const tb = parseTime(b.time);
    if (tb.minute !== ta.minute) return tb.minute - ta.minute;
    return tb.added - ta.added;
  });
};

export type KejadianType =
  | 'save'
  | 'foul'
  | 'yellow_card'
  | 'red_card'
  | 'clearance'
  | 'key_pass'
  | 'freestyle';

const KEJADIAN_OPTIONS: {
  type: KejadianType;
  label: string;
  description: string;
  icon: string;
  iconBg: string;
}[] = [
  {
    type: 'save',
    label: 'Penyelamatan (Save)',
    description: 'Kiper menggagalkan peluang gol',
    icon: 'ph:shield-check-bold',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  {
    type: 'foul',
    label: 'Pelanggaran (Foul)',
    description: 'Terjadi pelanggaran ringan',
    icon: 'ph:warning-bold',
    iconBg: 'bg-amber-100 text-amber-600',
  },
  {
    type: 'yellow_card',
    label: 'Kartu Kuning',
    description: 'Peringatan resmi dari wasit',
    icon: 'ph:rectangle-bold',
    iconBg: 'bg-amber-100 text-amber-500',
  },
  {
    type: 'red_card',
    label: 'Kartu Merah',
    description: 'Pemain harus meninggalkan lapangan',
    icon: 'ph:rectangle-bold',
    iconBg: 'bg-red-100 text-red-500',
  },
  {
    type: 'clearance',
    label: 'Clearance',
    description: 'Bola dibuang dari area berbahaya',
    icon: 'ph:shield-bold',
    iconBg: 'bg-blue-100 text-blue-600',
  },
  {
    type: 'key_pass',
    label: 'Key Pass',
    description: 'Umpan yang menciptakan peluang',
    icon: 'ph:arrow-elbow-down-right-bold',
    iconBg: 'bg-purple-100 text-purple-600',
  },
  {
    type: 'freestyle',
    label: 'Freestyle',
    description: 'Aksi individu yang menghibur',
    icon: 'ph:sparkle-bold',
    iconBg: 'bg-amber-100 text-amber-600',
  },
];

interface CatatKejadianModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameSlug: string;
  player: GameLineupPlayerItem | null;
  teamSide: 'home' | 'away';
  currentTimeline: GameTimelineEvent[];
  capturedTimeStr?: string;
  capturedSeconds?: number;
  onSuccess: () => void;
}

const CatatKejadianModal = ({
  isOpen,
  onClose,
  gameSlug,
  player,
  teamSide,
  currentTimeline,
  capturedTimeStr = '00:00',
  capturedSeconds = 0,
  onSuccess,
}: CatatKejadianModalProps) => {
  const [selectedType, setSelectedType] = useState<KejadianType | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedType('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSave = selectedType !== '' && player != null;

  const handleSave = async () => {
    if (!canSave || saving || !player) return;
    setError(null);
    setSaving(true);
    try {
      const calculatedMinute = Math.floor(capturedSeconds / 60);
      const timeTag = `${calculatedMinute}'`;

      const newEvent: GameTimelineEvent = {
        time: timeTag,
        type: selectedType,
        team_side: teamSide,
        player_id: player.player_participant_id,
      };

      const nextTimeline = sortTimelineByTimeDesc([...currentTimeline, newEvent]);
      await communityService.updateGameTimeline(gameSlug, nextTimeline);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || (err instanceof Error ? err.message : 'Gagal menyimpan kejadian'));
    } finally {
      setSaving(false);
    }
  };

  const playerLabel = player ? player.name : 'Catat Kejadian';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator */}
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

        {/* Header Title & Close Button */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-[#0E1726]">{playerLabel}</h2>
            <p className="text-xs font-semibold text-gray-400 mt-1">Catat kejadian pertandingan</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3.5 py-2.5 rounded-2xl">
            {error}
          </div>
        )}

        {/* Selectable Action List */}
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {KEJADIAN_OPTIONS.map((opt) => {
            const isSelected = selectedType === opt.type;
            return (
              <button
                key={opt.type}
                type="button"
                onClick={() => setSelectedType(opt.type)}
                className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left transition-all cursor-pointer ${
                  isSelected
                    ? 'border-[#FC480D] bg-orange-50/50 shadow-xs'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${opt.iconBg}`}
                  >
                    <Icon icon={opt.icon} className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold truncate ${
                        isSelected ? 'text-[#0E1726]' : 'text-gray-800'
                      }`}
                    >
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{opt.description}</p>
                  </div>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                    isSelected
                      ? 'border-[#FC480D] bg-[#FC480D] text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <Icon icon="ph:check-bold" className="w-3 h-3" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Timestamp Snapshot Display */}
        <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-2xl border border-gray-100">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
            <Icon icon="ph:clock-bold" className="w-4 h-4 text-[#FC480D]" />
            <span>Dicatat pada menit</span>
          </div>
          <span className="text-sm font-bold text-[#0E1726]">{capturedTimeStr}</span>
        </div>

        {/* Footer Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-11 px-4 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            className={`h-11 px-4 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-sm font-bold shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 ${
              !canSave || saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
            }`}
          >
            {saving ? (
              <>
                <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <span>Simpan</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatatKejadianModal;
